/**
 * 首页锚点 id 集合纯函数（F14）。
 *
 * HomePage 区锚点 id = 各 Section 区 type（`groupSectionsByType` 投影）；
 * F14 新增「科研机构」全宽区，根元素 `id="institutions"`，仅当
 * `SiteConfig.researchInstitutions` 非空时才存在 DOM。为让 AnchorNav
 * 一级项 `#institutions` 能通过 `selectAnchorNavTree` 过滤、IntersectionObserver
 * 能跟踪其高亮、初始 `/#institutions` 深链能滚动，需把 `institutions` 与
 * 各 type 一起喂给两处消费方（App.vue 的 `selectAnchorNavTree` 与 HomePage
 * 的 `useActiveAnchor` / `useInitialHashAnchorScroll`）。
 *
 * 集中为纯函数避免 App.vue 与 HomePage 各自重复 `groupSectionsByType(...).map`
 * + 漏掉 institutions 追加导致两处分叉（DRY）。readonly 入参 / readonly 返回，
 * 无 Vue / DOM 依赖，便于单测。
 */
import type { AggregateView } from '@/api/types';
import { groupSectionsByType } from '@/composables/group-sections';
import { isModuleSectionType } from '@/composables/module-target';

/** 科研机构区锚点 id（一级 NavItem `#institutions` 命中）。 */
export const INSTITUTIONS_ANCHOR_ID = 'institutions';
const HOME_HIDDEN_SECTION_TYPES = new Set(['material']);

/**
 * 返回首页锚点 id 列表：各区 type 按区间顺序 + 非空 researchInstitutions
 * 时末尾追加 `institutions`。aggregate 为 null 时返空数组。
 *
 * `researchInstitutions` 用 `?.` 容 null：wire 契约是非空数组，但与
 * HomePage `?? []` 接缝对齐，null 视同空（issue acceptance「空 / null
 * → 不渲染」的显式边界规则，非静默 fallback）。
 */
export function homeAnchorIds(aggregate: AggregateView | null): readonly string[] {
  if (aggregate === null) {
    return [];
  }
  const visibleSections = aggregate.sections.filter((section) =>
    !HOME_HIDDEN_SECTION_TYPES.has(section.type) && !isModuleSectionType(section.type));
  const groupTypes = groupSectionsByType(visibleSections).map((group) => group.type);
  const hasInstitutions = (aggregate.siteConfig.researchInstitutions?.length ?? 0) > 0;
  return hasInstitutions ? [...groupTypes, INSTITUTIONS_ANCHOR_ID] : groupTypes;
}
